<script setup lang="ts">
import type { Page } from '#shared/types/schema';
import Text from '~/components/base/Text.vue';
import Button from '~/components/base/BaseButton.vue';
import { ArrowRight } from 'lucide-vue-next';

interface BlockPagesPage {
	id: number;
	pages_id?: Page | string | null;
}

interface BlockPagesProps {
	data: {
		id?: string;
		tagline?: string | null;
		headline?: string | null;
		content?: string | null;
		pages?: BlockPagesPage[];
	};
}

const props = defineProps<BlockPagesProps>();

const { setAttr } = useVisualEditing();

// Filtra le pagine valide e le ordina
const validPages = computed(() => {
	if (!props.data.pages) return [];
	
	return props.data.pages
		.filter((pageItem): pageItem is BlockPagesPage & { pages_id: Page } => {
			return (
				pageItem.pages_id !== null &&
				typeof pageItem.pages_id === 'object' &&
				'permalink' in pageItem.pages_id
			);
		})
		.map((pageItem) => pageItem.pages_id as Page);
});

// Helper per verificare se una pagina ha un'immagine
const hasImage = (page: Page) => {
	return page.card_image !== null && page.card_image !== undefined;
};
</script>

<template>
	<section>
		<div class="text-center">
			<Tagline
				v-if="data.tagline"
				:tagline="data.tagline"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_pages',
								item: data.id,
								fields: 'tagline',
								mode: 'popover',
							})
						: undefined
				"
			/>
			<Headline
				v-if="data.headline"
				:headline="data.headline"
				text-color="text-black"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_pages',
								item: data.id,
								fields: 'headline',
								mode: 'popover',
							})
						: undefined
				"
			/>
			<Text
				v-if="data.content"
				:content="data.content"
				text-color="text-black"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_pages',
								item: data.id,
								fields: 'content',
								mode: 'drawer',
							})
						: undefined
				"
				class="mt-6 max-w-none w-full prose-p:text-center prose-headings:text-center prose-ul:text-center prose-ol:text-center prose-li:text-center prose-blockquote:text-center [&_p]:!mb-2 [&_p+_p]:!mt-2"
			/>
		</div>

		<div
			v-if="validPages.length > 0"
			class="grid gap-8 mt-8"
			:class="{
				'grid-cols-1 justify-items-center': validPages.length === 1,
				'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2': validPages.length > 1,
			}"
			:data-directus="
				data.id
					? setAttr({
							collection: 'block_pages',
							item: data.id,
							fields: ['pages'],
							mode: 'modal',
						})
					: undefined
			"
		>
			<NuxtLink
				v-for="(page, index) in validPages"
				:key="page.id"
				:to="page.permalink"
				:class="[
					'group block overflow-hidden relative w-full',
					hasImage(page) 
						? 'aspect-[2/3] lg:max-h-[500px] lg:max-w-[333px]' 
						: 'bg-white',
					hasImage(page) && validPages.length > 1 && index % 2 === 0 ? 'lg:justify-self-end' : hasImage(page) && validPages.length > 1 ? 'lg:justify-self-start' : ''
				]"
			>
				<div v-if="hasImage(page)" class="absolute inset-0 w-full h-full">
					<DirectusImage
						:uuid="typeof page.card_image === 'string' ? page.card_image : page.card_image?.id"
						:alt="page.card_title || page.title"
						class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
						sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
					/>
					<!-- Overlay scuro per migliorare la leggibilità del testo -->
					<div class="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>
				</div>
				<div 
					:class="[
						'relative p-6 flex flex-col',
						hasImage(page) 
							? 'h-full justify-end text-center' 
							: 'min-h-0 justify-start text-left'
					]"
				>
					<h3
						v-if="page.card_title || page.title"
						:class="[
							'text-xl font-heading',
							hasImage(page) ? 'text-white' : 'text-black'
						]"
					>
						{{ page.card_title || page.title }}
					</h3>
					<p
						v-if="page.card_abstract"
						:class="[
							'text-sm mt-2',
							hasImage(page) ? 'text-white line-clamp-4' : 'text-black'
						]"
					>
						{{ page.card_abstract }}
					</p>
					<div 
						:class="[
							'mt-4 flex',
							hasImage(page) ? 'justify-center' : 'justify-start'
						]"
					>
						<div
							:class="[
								'px-4 py-2 text-center rounded-none font-button font-medium transition-colors flex items-center justify-center gap-2',
								hasImage(page) 
									? 'bg-accent text-white group-hover:opacity-90' 
									: 'bg-black text-white hover:bg-black/90'
							]"
						>
							<span v-if="page.card_button_text">{{ page.card_button_text }}</span>
							<ArrowRight v-if="!page.card_button_text" class="size-4 shrink-0" />
						</div>
					</div>
				</div>
			</NuxtLink>
		</div>
	</section>
</template>

